import React, { memo } from 'react';
import { Box, Typography, Chip, Skeleton, Stack } from '@mui/material';
import { People } from '@mui/icons-material';
import { EmployeeRole } from '../../types/dashboardTypes';
import { getHashedColor } from '../../utils/colorUtils';
import { DashboardCard, DashboardSkeletonCard, useDashboardAccent, useDashboardTones } from './DashboardCard';

interface EmployeeSummaryCardProps {
    total: number;
    studentCount: number;
    byRole: EmployeeRole[];
}

/**
 * Elegant People Overview Card
 * Shows student count, employee count, and role breakdown
 * Supports light and dark themes with refined aesthetics
 */
const EmployeeSummaryCard: React.FC<EmployeeSummaryCardProps> = memo(({
    total,
    studentCount,
    byRole
}) => {
    const { isDark, line } = useDashboardTones();
    const students = useDashboardAccent('info');
    const employees = useDashboardAccent('success');

    return (
        <DashboardCard
            accent="success"
            icon={People}
            title="People Overview"
            subtitle="Students & employees at your school"
        >
            {/* Stats Row */}
            <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: line }}>
                <Box
                    sx={{
                        flex: 1,
                        py: 1.5,
                        px: 2,
                        textAlign: 'center',
                        borderRight: '1px solid',
                        borderColor: line
                    }}
                >
                    <Typography variant="h5" fontWeight={600} sx={{ color: students.text, letterSpacing: -0.5 }}>
                        {studentCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        Students
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, py: 1.5, px: 2, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={600} sx={{ color: employees.text, letterSpacing: -0.5 }}>
                        {total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        Employees
                    </Typography>
                </Box>
            </Box>

            {/* Role Breakdown */}
            <Box sx={{ p: 1.5, flex: 1 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                        textTransform: 'uppercase', 
                        letterSpacing: 0.5, 
                        fontSize: 10,
                        fontWeight: 500
                    }}
                >
                    By Role
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
                    {byRole.map((role) => {
                        const colors = getHashedColor(role.roleName, isDark);
                        return (
                            <Chip
                                key={role.roleId}
                                label={`${role.roleName}: ${role.count}`}
                                size="small"
                                sx={{
                                    bgcolor: colors.bg,
                                    color: colors.text,
                                    fontWeight: 500,
                                    fontSize: 11,
                                    borderRadius: 1.5,
                                    height: 24,
                                    '& .MuiChip-label': {
                                        px: 1
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>
            </Box>
        </DashboardCard>
    );
});

EmployeeSummaryCard.displayName = 'EmployeeSummaryCard';

export const EmployeeSummaryCardSkeleton: React.FC = memo(() => (
    <DashboardSkeletonCard sx={{ height: '100%' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
                <Skeleton variant="rounded" width={36} height={36} />
                <Box>
                    <Skeleton width={100} height={16} />
                    <Skeleton width={140} height={12} />
                </Box>
            </Box>
        </Box>
        <Box display="flex" borderBottom="1px solid" borderColor="divider">
            <Box flex={1} py={1.5} px={2} textAlign="center">
                <Skeleton width={40} height={28} sx={{ mx: 'auto' }} />
                <Skeleton width={45} height={12} sx={{ mx: 'auto' }} />
            </Box>
            <Box flex={1} py={1.5} px={2} textAlign="center">
                <Skeleton width={40} height={28} sx={{ mx: 'auto' }} />
                <Skeleton width={55} height={12} sx={{ mx: 'auto' }} />
            </Box>
        </Box>
        <Box p={1.5}>
            <Skeleton width={50} height={10} />
            <Box display="flex" gap={0.75} mt={1} flexWrap="wrap">
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" width={70} height={24} sx={{ borderRadius: 1.5 }} />
                ))}
            </Box>
        </Box>
    </DashboardSkeletonCard>
));

EmployeeSummaryCardSkeleton.displayName = 'EmployeeSummaryCardSkeleton';

export default EmployeeSummaryCard;
